import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Plus,
  Edit,
  Trash2,
  Loader2,
  GripVertical,
  ChevronDown,
  Upload,
  ChevronsUpDown,
  Check,
  RotateCcw,
} from "lucide-react";
import { BioIconPicker } from "./BioIconPicker";
import { BIO_ICONS, getBioIcon, type BioIconKey } from "@/lib/bioIcons";
import { useImageUpload } from "@/hooks/useImageUpload";
import { extractYouTubeId } from "@/lib/youtube";
import {
  useBioLinks,
  useCreateBioLink,
  useUpdateBioLink,
  useDeleteBioLink,
  useToggleBioLinkActive,
  useReorderBioLinks,
  useBioLinkClickStats,
  usePublishedBlogPosts,
  type BioLink,
  type BioLinkKind,
  type BioVideoProvider,
} from "@/hooks/useBioLinks";
import { useBioProfile, useUpdateBioProfile } from "@/hooks/useBioProfile";
import { BIO_GRADIENT_PRESETS, BIO_REDESIGN_DEFAULTS } from "@/lib/bioTheme";
import { BioView } from "@/components/bio/BioView";
import { cn } from "@/lib/utils";

const KIND_LABELS: Record<BioLinkKind, string> = {
  link: "Link",
  video: "Vídeo",
  blog_post: "Post de blog",
  social: "Rede social",
};

function detectVideoProvider(url: string): BioVideoProvider | null {
  try {
    const host = new URL(url.trim()).hostname.replace(/^www\./, "");
    if (extractYouTubeId(url)) return "youtube";
    if (host === "instagram.com" || host.endsWith(".instagram.com")) return "instagram";
    if (host === "tiktok.com" || host.endsWith(".tiktok.com")) return "tiktok";
    return null;
  } catch {
    return null;
  }
}

interface LinkFormData {
  kind: BioLinkKind;
  title: string;
  description: string;
  url: string;
  icon: BioIconKey;
  video_url: string;
  blog_post_id: string;
  is_active: boolean;
  is_primary: boolean;
}

const defaultFormData: LinkFormData = {
  kind: "link",
  title: "",
  description: "",
  url: "",
  icon: "link",
  video_url: "",
  blog_post_id: "",
  is_active: true,
  is_primary: false,
};

interface LinksManagerProps {
  /** Rendered inside ContentHub — hides the standalone header. */
  embedded?: boolean;
}

export function LinksManager({ embedded = false }: LinksManagerProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <div className="flex flex-col gap-[22px]">
      {!embedded && (
        <div>
          <h1 className="mb-1.5 font-display text-[27px] font-bold tracking-[-0.03em]">
            Links (Bio)
          </h1>
          <p className="text-[13.5px] text-muted-foreground">
            Gerencie os links da sua página em{" "}
            <a href="/bio" target="_blank" rel="noopener noreferrer" className="underline">
              /bio
            </a>
          </p>
        </div>
      )}

      <LinksTab />

      <Collapsible open={settingsOpen} onOpenChange={setSettingsOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 text-[12.5px] font-semibold text-muted-foreground hover:text-foreground"
          >
            <ChevronDown
              className={cn("h-4 w-4 transition-transform", settingsOpen && "rotate-180")}
            />
            Configurações da página (perfil, fundo, botões)
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4">
          <AppearanceTab />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

// ─── Links tab ──────────────────────────────────────────────────────────────

function LinksTab() {
  const { data: links, isLoading } = useBioLinks();
  const { data: clickStats } = useBioLinkClickStats();
  const createLink = useCreateBioLink();
  const updateLink = useUpdateBioLink();
  const deleteLink = useDeleteBioLink();
  const toggleActive = useToggleBioLinkActive();
  const reorderLinks = useReorderBioLinks();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<BioLink | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const mainLinks = (links || [])
    .filter((l) => l.kind !== "social")
    .sort((a, b) => a.display_order - b.display_order);
  const socialLinks = (links || [])
    .filter((l) => l.kind === "social")
    .sort((a, b) => a.display_order - b.display_order);

  const totalClicks = Object.values(clickStats ?? {}).reduce((s, n) => s + n, 0);

  const handleOpenDialog = (link?: BioLink) => {
    setEditingLink(link || null);
    setDialogOpen(true);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.destination.droppableId !== result.source.droppableId) return;

    const zone = result.destination.droppableId as "main" | "social";
    const list = zone === "main" ? mainLinks : socialLinks;
    const reordered = Array.from(list);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    reorderLinks.mutate(reordered.map((item, index) => ({ id: item.id, display_order: index })));
  };

  const handleSave = async (data: LinkFormData) => {
    const payload = {
      kind: data.kind,
      title: data.kind === "blog_post" ? undefined : data.title.trim() || undefined,
      description: data.description.trim() || undefined,
      url: data.kind === "link" || data.kind === "social" ? data.url.trim() : undefined,
      icon: data.kind === "blog_post" ? undefined : data.icon,
      video_url: data.kind === "video" ? data.video_url.trim() : undefined,
      video_provider:
        data.kind === "video" ? detectVideoProvider(data.video_url) || undefined : undefined,
      blog_post_id: data.kind === "blog_post" ? data.blog_post_id || undefined : undefined,
      is_active: data.is_active,
      is_primary: data.kind === "link" ? data.is_primary : false,
    };

    if (editingLink) {
      await updateLink.mutateAsync({ id: editingLink.id, data: payload });
    } else {
      const nextOrder = data.kind === "social" ? socialLinks.length : mainLinks.length;
      await createLink.mutateAsync({ ...payload, display_order: nextOrder });
    }
    setDialogOpen(false);
    setEditingLink(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <DragDropContext onDragEnd={handleDragEnd}>
        <LinkZone
          title="Links da bio"
          meta={`${totalClicks.toLocaleString("pt-BR")} cliques no mês`}
          action={
            <Button
              className="h-8 rounded-lg px-[13px] text-xs font-semibold"
              onClick={() => handleOpenDialog()}
            >
              Novo link
            </Button>
          }
          droppableId="main"
          items={mainLinks}
          clickStats={clickStats}
          onEdit={handleOpenDialog}
          onDelete={setDeleteId}
          onToggleActive={(link) =>
            toggleActive.mutate({ id: link.id, currentActive: link.is_active })
          }
          emptyLabel="Nenhum link cadastrado ainda."
        />

        <LinkZone
          title="Redes sociais"
          meta={`${socialLinks.length} ${socialLinks.length === 1 ? "rede" : "redes"}`}
          droppableId="social"
          items={socialLinks}
          clickStats={clickStats}
          onEdit={handleOpenDialog}
          onDelete={setDeleteId}
          onToggleActive={(link) =>
            toggleActive.mutate({ id: link.id, currentActive: link.is_active })
          }
          emptyLabel="Nenhuma rede social cadastrada ainda."
        />
      </DragDropContext>

      <LinkFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingLink={editingLink}
        onSave={handleSave}
        saving={createLink.isPending || updateLink.isPending}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir link</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este link? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleteId) await deleteLink.mutateAsync(deleteId);
                setDeleteId(null);
              }}
              className="bg-bad text-bad-foreground hover:bg-bad/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function LinkZone({
  title,
  meta,
  action,
  droppableId,
  items,
  clickStats,
  onEdit,
  onDelete,
  onToggleActive,
  emptyLabel,
}: {
  title: string;
  meta?: string;
  action?: React.ReactNode;
  droppableId: string;
  items: BioLink[];
  clickStats?: Record<string, number>;
  onEdit: (link: BioLink) => void;
  onDelete: (id: string) => void;
  onToggleActive: (link: BioLink) => void;
  emptyLabel: string;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
      <div className="flex items-center justify-between gap-3 border-b border-border px-[22px] pb-3.5 pt-[18px]">
        <h2 className="text-[15px] font-bold">{title}</h2>
        <div className="flex items-center gap-3">
          {meta && <span className="text-xs text-muted-foreground">{meta}</span>}
          {action}
        </div>
      </div>
      <Droppable droppableId={droppableId}>
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {items.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">{emptyLabel}</p>
            )}
            {items.map((link, index) => (
              <Draggable key={link.id} draggableId={link.id} index={index}>
                {(drag, snapshot) => (
                  <div
                    ref={drag.innerRef}
                    {...drag.draggableProps}
                    className={cn(
                      "flex items-center gap-4 border-b border-border bg-card px-[22px] py-[15px] last:border-b-0 hover:bg-muted/40",
                      snapshot.isDragging && "rounded-xl opacity-80 shadow-lg",
                    )}
                  >
                    <div
                      {...drag.dragHandleProps}
                      className="flex cursor-grab text-muted-foreground"
                    >
                      <GripVertical className="h-[15px] w-[15px]" />
                    </div>
                    <LinkRowIcon link={link} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 truncate text-[13.5px] font-semibold">
                        <span className="truncate">
                          {link.kind === "blog_post"
                            ? link.blog_posts?.title || "(post removido)"
                            : link.title || "(sem título)"}
                        </span>
                        {link.is_primary && (
                          <span className="inline-flex h-[19px] shrink-0 items-center rounded-[6px] bg-accent px-1.5 text-[10.5px] font-bold text-primary">
                            Destaque
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 truncate text-[11.5px] text-muted-foreground">
                        {link.description ? <span>{link.description} · </span> : null}
                        <span className="font-mono">
                          {link.kind === "video"
                            ? link.video_url
                            : link.kind === "blog_post"
                              ? `/blog/${link.blog_posts?.slug ?? ""}`
                              : link.url}
                        </span>
                      </div>
                    </div>
                    <span className="shrink-0 text-[12.5px] text-muted-foreground">
                      {(clickStats?.[link.id] || 0).toLocaleString("pt-BR")} cliques
                    </span>
                    <Switch checked={link.is_active} onCheckedChange={() => onToggleActive(link)} />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onEdit(link)}
                      aria-label="Editar link"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-bad hover:text-bad"
                      onClick={() => onDelete(link.id)}
                      aria-label="Excluir link"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </section>
  );
}

function LinkRowIcon({ link }: { link: BioLink }) {
  const { Icon } = getBioIcon(link.icon);
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-muted">
      <Icon className="h-4 w-4" />
    </div>
  );
}

// ─── Create/edit dialog ─────────────────────────────────────────────────────

function LinkFormDialog({
  open,
  onOpenChange,
  editingLink,
  onSave,
  saving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingLink: BioLink | null;
  onSave: (data: LinkFormData) => Promise<void>;
  saving: boolean;
}) {
  const [formData, setFormData] = useState<LinkFormData>(defaultFormData);
  const { data: blogPosts } = usePublishedBlogPosts();

  useEffect(() => {
    if (!open) return;
    if (editingLink) {
      setFormData({
        kind: editingLink.kind,
        title: editingLink.title || "",
        description: editingLink.description || "",
        url: editingLink.url || "",
        icon: (editingLink.icon as BioIconKey) || "link",
        video_url: editingLink.video_url || "",
        blog_post_id: editingLink.blog_post_id || "",
        is_active: editingLink.is_active,
        is_primary: editingLink.is_primary ?? false,
      });
    } else {
      setFormData(defaultFormData);
    }
  }, [open, editingLink]);

  const handleKindChange = (kind: BioLinkKind) => {
    const defaultIcon: BioIconKey =
      kind === "video"
        ? "video"
        : kind === "blog_post"
          ? "blog"
          : kind === "social"
            ? "whatsapp"
            : "link";
    setFormData((prev) => ({ ...prev, kind, icon: defaultIcon }));
  };

  const videoProvider = formData.kind === "video" ? detectVideoProvider(formData.video_url) : null;
  const youtubeId = formData.kind === "video" ? extractYouTubeId(formData.video_url) : null;

  const isValid =
    (formData.kind === "link" && formData.title.trim() && formData.url.trim()) ||
    (formData.kind === "social" && formData.url.trim()) ||
    (formData.kind === "video" && formData.video_url.trim() && !!videoProvider) ||
    (formData.kind === "blog_post" && !!formData.blog_post_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingLink ? "Editar Link" : "Novo Link"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="bio-tipo">Tipo</Label>
            <Select value={formData.kind} onValueChange={(v) => handleKindChange(v as BioLinkKind)}>
              <SelectTrigger id="bio-tipo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="link">Link</SelectItem>
                <SelectItem value="video">Vídeo (YouTube/Instagram/TikTok)</SelectItem>
                <SelectItem value="blog_post">Post de blog</SelectItem>
                <SelectItem value="social">Rede social</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(formData.kind === "link" || formData.kind === "social") && (
            <>
              <div className="space-y-2">
                <Label htmlFor="link-title">Título {formData.kind === "link" && "*"}</Label>
                <Input
                  id="link-title"
                  placeholder={
                    formData.kind === "social" ? "Ex: WhatsApp (opcional)" : "Ex: Meu portfólio"
                  }
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </div>
              {formData.kind === "link" && (
                <div className="space-y-2">
                  <Label htmlFor="link-description">Subtítulo</Label>
                  <Input
                    id="link-description"
                    placeholder="Ex: 30 minutos, sem compromisso"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                  <p className="text-[11.5px] text-muted-foreground">
                    Linha menor abaixo do título, como no protótipo.
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="link-url">URL *</Label>
                <Input
                  id="link-url"
                  placeholder="https://..."
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <span id="bio-icon-label" className="text-sm font-medium leading-none">
                  Ícone
                </span>
                <BioIconPicker
                  aria-labelledby="bio-icon-label"
                  value={formData.icon}
                  onChange={(icon) => setFormData({ ...formData, icon })}
                />
              </div>
              {formData.kind === "link" && (
                <div className="flex items-center space-x-2">
                  <Switch
                    id="link-primary"
                    checked={formData.is_primary}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_primary: checked })}
                  />
                  <Label htmlFor="link-primary">
                    Link em destaque (cartão na cor de destaque — só um por vez)
                  </Label>
                </div>
              )}
            </>
          )}

          {formData.kind === "video" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="video-title">Legenda (opcional)</Label>
                <Input
                  id="video-title"
                  placeholder="Ex: Veja como eu trabalho"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="video-url">URL do vídeo *</Label>
                <Input
                  id="video-url"
                  placeholder="Link do YouTube, Instagram ou TikTok"
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                />
                {formData.video_url.trim() && !videoProvider && (
                  <p className="text-xs text-destructive">
                    Não foi possível reconhecer essa URL como YouTube, Instagram ou TikTok.
                  </p>
                )}
                {videoProvider === "youtube" && youtubeId && (
                  <img
                    src={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`}
                    alt="Preview do vídeo"
                    className="mt-1 max-h-32 rounded-md border"
                  />
                )}
                {(videoProvider === "instagram" || videoProvider === "tiktok") && (
                  <p className="text-xs text-muted-foreground">
                    Vídeo do {videoProvider === "instagram" ? "Instagram" : "TikTok"} reconhecido —
                    o embed será carregado na página pública.
                  </p>
                )}
              </div>
            </>
          )}

          {formData.kind === "blog_post" && (
            <div className="space-y-2">
              <span id="bio-post-label" className="text-sm font-medium leading-none">
                Post de blog *
              </span>
              <BlogPostCombobox
                aria-labelledby="bio-post-label"
                posts={blogPosts || []}
                value={formData.blog_post_id}
                onChange={(id) => setFormData({ ...formData, blog_post_id: id })}
              />
            </div>
          )}

          <div className="flex items-center space-x-2">
            <Switch
              id="link-active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <Label htmlFor="link-active">Ativo (visível na página pública)</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={() => onSave(formData)} disabled={saving || !isValid}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {editingLink ? "Salvar Alterações" : "Criar Link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BlogPostCombobox({
  posts,
  value,
  onChange,
  "aria-labelledby": labelledBy,
}: {
  posts: { id: string; title: string; slug: string; cover_image_url: string | null }[];
  value: string;
  onChange: (id: string) => void;
  "aria-labelledby"?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = posts.find((p) => p.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-labelledby={labelledBy}
          variant="outline"
          role="combobox"
          className="w-full justify-between font-normal"
        >
          {selected ? selected.title : "Selecione um post publicado..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command>
          <CommandInput placeholder="Buscar post..." />
          <CommandList>
            <CommandEmpty>Nenhum post publicado encontrado.</CommandEmpty>
            <CommandGroup>
              {posts.map((post) => (
                <CommandItem
                  key={post.id}
                  value={post.title}
                  onSelect={() => {
                    onChange(post.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn("mr-2 h-4 w-4", value === post.id ? "opacity-100" : "opacity-0")}
                  />
                  {post.title}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─── Appearance tab ─────────────────────────────────────────────────────────

function AppearanceTab() {
  const { data: profile, isLoading } = useBioProfile();
  const updateProfile = useUpdateBioProfile();
  const { uploadImage, uploading } = useImageUpload();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const bgInputRef = useRef<HTMLInputElement>(null);

  const { data: allLinks } = useBioLinks();
  const previewLinks = useMemo(() => (allLinks || []).filter((l) => l.is_active), [allLinks]);

  const [form, setForm] = useState({
    display_name: "",
    tagline: "",
    avatar_url: "",
    background_style: BIO_REDESIGN_DEFAULTS.background_style,
    background_color: BIO_REDESIGN_DEFAULTS.background_color,
    background_color_end: "#F1ECFE",
    background_image_url: "",
    background_overlay_opacity: BIO_REDESIGN_DEFAULTS.background_overlay_opacity,
    button_style: BIO_REDESIGN_DEFAULTS.button_style,
    button_color: BIO_REDESIGN_DEFAULTS.button_color,
    button_text_color: BIO_REDESIGN_DEFAULTS.button_text_color,
    button_radius: BIO_REDESIGN_DEFAULTS.button_radius,
    text_color: BIO_REDESIGN_DEFAULTS.text_color,
    muted_text_color: BIO_REDESIGN_DEFAULTS.muted_text_color,
    subtle_text_color: BIO_REDESIGN_DEFAULTS.subtle_text_color,
    card_color: BIO_REDESIGN_DEFAULTS.card_color,
    card_border_color: BIO_REDESIGN_DEFAULTS.card_border_color,
    social_style: BIO_REDESIGN_DEFAULTS.social_style,
    footer_text: BIO_REDESIGN_DEFAULTS.footer_text,
    show_footer: BIO_REDESIGN_DEFAULTS.show_footer,
    is_published: true,
  });

  useEffect(() => {
    if (!profile) return;
    const d = BIO_REDESIGN_DEFAULTS;
    setForm({
      display_name: profile.display_name,
      tagline: profile.tagline || "",
      avatar_url: profile.avatar_url || "",
      background_style: profile.background_style,
      background_color: profile.background_color,
      background_color_end: profile.background_color_end || "#F1ECFE",
      background_image_url: profile.background_image_url || "",
      background_overlay_opacity: profile.background_overlay_opacity,
      button_style: profile.button_style,
      button_color: profile.button_color,
      button_text_color: profile.button_text_color,
      button_radius: profile.button_radius,
      text_color: profile.text_color || d.text_color,
      muted_text_color: profile.muted_text_color || d.muted_text_color,
      subtle_text_color: profile.subtle_text_color || d.subtle_text_color,
      card_color: profile.card_color || d.card_color,
      card_border_color: profile.card_border_color || d.card_border_color,
      social_style: profile.social_style || d.social_style,
      footer_text: profile.footer_text ?? d.footer_text,
      show_footer: profile.show_footer ?? d.show_footer,
      is_published: profile.is_published,
    });
  }, [profile]);

  const restoreRedesign = () => {
    setForm((prev) => ({
      ...prev,
      ...BIO_REDESIGN_DEFAULTS,
      background_color_end: "#F1ECFE",
      background_image_url: "",
    }));
    toast.info("Padrão do redesign aplicado — clique em Salvar Aparência para publicar.");
  };

  const handleAvatarSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file, "bio-images");
    if (url) setForm((prev) => ({ ...prev, avatar_url: url }));
    if (avatarInputRef.current) avatarInputRef.current.value = "";
  };

  const handleBgImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file, "bio-images");
    if (url) setForm((prev) => ({ ...prev, background_image_url: url }));
    if (bgInputRef.current) bgInputRef.current.value = "";
  };

  const handleSave = () => {
    updateProfile.mutate({
      ...form,
      tagline: form.tagline.trim() || undefined,
      footer_text: form.footer_text.trim(),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
      <div className="space-y-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Perfil</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <span id="bio-avatar-label" className="text-sm font-medium leading-none">
                Avatar
              </span>
              <div
                className="flex items-center gap-3"
                role="group"
                aria-labelledby="bio-avatar-label"
              >
                {form.avatar_url && (
                  <img
                    src={form.avatar_url}
                    alt="Avatar"
                    className="h-14 w-14 rounded-full object-cover"
                  />
                )}
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarSelect}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-4 w-4" />
                  )}
                  Enviar foto
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="display-name">Nome</Label>
              <Input
                id="display-name"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tagline">Bio / Tagline</Label>
              <Textarea
                id="tagline"
                rows={2}
                placeholder="Ex: Desenvolvedor Full Stack | Automação & CRM"
                value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="is-published"
                checked={form.is_published}
                onCheckedChange={(checked) => setForm({ ...form, is_published: checked })}
              />
              <Label htmlFor="is-published">Página publicada</Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Fundo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bio-estilo">Estilo</Label>
              <Select
                value={form.background_style}
                onValueChange={(v) =>
                  setForm({ ...form, background_style: v as typeof form.background_style })
                }
              >
                <SelectTrigger id="bio-estilo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="solid">Cor sólida</SelectItem>
                  <SelectItem value="gradient">Gradiente</SelectItem>
                  <SelectItem value="image">Imagem</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.background_style === "gradient" && (
              <div className="space-y-2">
                <span id="bio-presets-label" className="text-sm font-medium leading-none">
                  Predefinidos
                </span>
                <div
                  className="grid grid-cols-4 gap-2"
                  role="group"
                  aria-labelledby="bio-presets-label"
                >
                  {BIO_GRADIENT_PRESETS.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() =>
                        setForm({
                          ...form,
                          background_color: preset.from,
                          background_color_end: preset.to,
                        })
                      }
                      className="group space-y-1"
                    >
                      <div
                        className="h-8 w-full rounded-md border transition-shadow group-hover:ring-2 group-hover:ring-ring"
                        style={{
                          background: `linear-gradient(90deg, ${preset.from}, ${preset.to})`,
                        }}
                      />
                      <p className="truncate text-center text-[10px] text-muted-foreground">
                        {preset.label}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {form.background_style !== "image" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bio-cor">
                    Cor {form.background_style === "gradient" && "(início)"}
                  </Label>
                  <Input
                    id="bio-cor"
                    type="color"
                    className="h-10 p-1"
                    value={form.background_color}
                    onChange={(e) => setForm({ ...form, background_color: e.target.value })}
                  />
                </div>
                {form.background_style === "gradient" && (
                  <div className="space-y-2">
                    <Label htmlFor="bio-cor-fim">Cor (fim)</Label>
                    <Input
                      id="bio-cor-fim"
                      type="color"
                      className="h-10 p-1"
                      value={form.background_color_end}
                      onChange={(e) => setForm({ ...form, background_color_end: e.target.value })}
                    />
                  </div>
                )}
              </div>
            )}

            {form.background_style === "image" && (
              <div className="space-y-2">
                <span id="bio-bg-image-label" className="text-sm font-medium leading-none">
                  Imagem de fundo
                </span>
                <div
                  className="flex items-center gap-3"
                  role="group"
                  aria-labelledby="bio-bg-image-label"
                >
                  {form.background_image_url && (
                    <img
                      src={form.background_image_url}
                      alt="Fundo"
                      className="h-16 w-16 rounded-md object-cover"
                    />
                  )}
                  <input
                    ref={bgInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleBgImageSelect}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => bgInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="mr-2 h-4 w-4" />
                    )}
                    Enviar imagem
                  </Button>
                </div>
                <Label htmlFor="bio-opacidade-do-overlay-legibil" className="mt-2 block">
                  Opacidade do overlay (legibilidade)
                </Label>
                <Input
                  id="bio-opacidade-do-overlay-legibil"
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={form.background_overlay_opacity}
                  onChange={(e) =>
                    setForm({ ...form, background_overlay_opacity: Number(e.target.value) })
                  }
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Botões</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bio-estilo-2">Estilo</Label>
                <Select
                  value={form.button_style}
                  onValueChange={(v) =>
                    setForm({ ...form, button_style: v as typeof form.button_style })
                  }
                >
                  <SelectTrigger id="bio-estilo-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="filled">Preenchido</SelectItem>
                    <SelectItem value="outline">Contorno</SelectItem>
                    <SelectItem value="soft">Suave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bio-formato">Formato</Label>
                <Select
                  value={form.button_radius}
                  onValueChange={(v) =>
                    setForm({ ...form, button_radius: v as typeof form.button_radius })
                  }
                >
                  <SelectTrigger id="bio-formato">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Reto</SelectItem>
                    <SelectItem value="md">Levemente arredondado</SelectItem>
                    <SelectItem value="lg">Arredondado</SelectItem>
                    <SelectItem value="full">Pílula</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[11.5px] text-muted-foreground">
              O estilo e o formato valem para o link em destaque; o formato também define o raio dos
              demais cartões.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Cores</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <ColorField
                label="Destaque (link principal)"
                value={form.button_color}
                onChange={(v) => setForm({ ...form, button_color: v })}
              />
              <ColorField
                label="Texto do destaque"
                value={form.button_text_color}
                onChange={(v) => setForm({ ...form, button_text_color: v })}
              />
              <ColorField
                label="Texto principal"
                value={form.text_color}
                onChange={(v) => setForm({ ...form, text_color: v })}
              />
              <ColorField
                label="Texto secundário (tagline)"
                value={form.muted_text_color}
                onChange={(v) => setForm({ ...form, muted_text_color: v })}
              />
              <ColorField
                label="Texto discreto (subtítulo, rodapé)"
                value={form.subtle_text_color}
                onChange={(v) => setForm({ ...form, subtle_text_color: v })}
              />
              <ColorField
                label="Cartão"
                value={form.card_color}
                onChange={(v) => setForm({ ...form, card_color: v })}
              />
              <ColorField
                label="Borda do cartão"
                value={form.card_border_color}
                onChange={(v) => setForm({ ...form, card_border_color: v })}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Redes e rodapé</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bio-icones-das-redes-sociais">Ícones das redes sociais</Label>
              <Select
                value={form.social_style}
                onValueChange={(v) =>
                  setForm({ ...form, social_style: v as typeof form.social_style })
                }
              >
                <SelectTrigger id="bio-icones-das-redes-sociais">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="abbr">
                    Abreviação (in, ig, gh…) — igual ao protótipo
                  </SelectItem>
                  <SelectItem value="icon">Ícone da rede</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="footer-text">Texto do rodapé</Label>
              <Input
                id="footer-text"
                value={form.footer_text}
                onChange={(e) => setForm({ ...form, footer_text: e.target.value })}
                placeholder="eliveutonsouza.com"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="show-footer"
                checked={form.show_footer}
                onCheckedChange={(checked) => setForm({ ...form, show_footer: checked })}
              />
              <Label htmlFor="show-footer">Mostrar rodapé</Label>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={handleSave} disabled={updateProfile.isPending}>
            {updateProfile.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar Aparência
          </Button>
          <Button type="button" variant="outline" onClick={restoreRedesign}>
            <RotateCcw className="mr-2 h-4 w-4" /> Restaurar padrão do redesign
          </Button>
        </div>
      </div>

      <div className="h-fit lg:sticky lg:top-6">
        <p className="mb-2 text-sm font-medium">Pré-visualização</p>
        <p className="mb-3 text-[11.5px] text-muted-foreground">
          Mesmo componente da página pública — o que aparece aqui é o que vai ao ar.
        </p>
        <div className="overflow-hidden rounded-2xl border border-border">
          <BioView profile={form} links={previewLinks} preview />
        </div>
      </div>
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <span className="text-sm font-medium leading-none">{label}</span>
      <div className="flex items-center gap-2" role="group" aria-label={label}>
        <Input
          type="color"
          className="h-10 w-14 shrink-0 p-1"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <Input
          className="h-10 font-mono text-xs uppercase"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={7}
        />
      </div>
    </div>
  );
}
